import { Edit03Icon } from '@langchain/untitled-ui-icons';
import { RowSelectionState } from '@tanstack/react-table';

import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Text } from '@/design-system';
import {
  useCreateAnnotationQueueRunsMutation,
  useOrganizationId,
  usePopulateAnnotationQueueMutation,
  useRunRules,
  useRunRulesMutation,
} from '@/hooks/useSwr';
import {
  apiAnnotationQueuesPath,
  appAnnotationQueuesPath,
  appDatasetsPath,
  appEvaluatorPath,
  appOrganizationPath,
  appProjectsPath,
  appSessionPath,
} from '@/utils/constants';
import { cn } from '@/utils/tailwind';

import { FloatingActionBar } from '../FloatingActionBar';
import useToast from '../Toast/useToast';

type AlignmentModeActionsProps = {
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  alignmentQueueId?: string;
  runRuleId?: string;
  sessionId?: string;
  datasetId?: string;
  setAlignmentMode?: (value: boolean) => void;
};

export const AlignmentModeActions: FC<AlignmentModeActionsProps> = ({
  rowSelection,
  setRowSelection,
  alignmentQueueId,
  runRuleId,
  sessionId,
  datasetId,
  setAlignmentMode,
}) => {
  const navigate = useNavigate();
  const organizationId = useOrganizationId();
  const { createToast } = useToast();

  // Alignment mode mutation hooks
  const { trigger: populateQueueTrigger } =
    usePopulateAnnotationQueueMutation();
  const {
    trigger: alignEvaluatorTrigger,
    isMutating: isAlignEvaluatorMutating,
  } = usePopulateAnnotationQueueMutation();
  const {
    trigger: createAnnotationQueueRuns,
    isMutating: isCreatingAnnotationQueueRuns,
  } = useCreateAnnotationQueueRunsMutation();

  // Fetch run rule data for alignment mode
  const { data: runRules } = useRunRules({
    id: [runRuleId ?? ''],
  });
  const runRule = runRules?.[0];
  const mutate = useRunRulesMutation(runRule?.id, {
    onError: (error) => {
      createToast({
        title: 'Error creating annotation queue',
        description: error.message,
        error: true,
      });
    },
  });

  // Handle annotation queue population for alignment mode
  const handlePopulateAnnotationQueue = useCallback(
    (aq_id: string, isAlignEvaluator?: boolean) => {
      const shouldUseCreateAnnotationQueueRuns = isAlignEvaluator && sessionId;

      if (shouldUseCreateAnnotationQueueRuns) {
        // createAnnotationQueueRuns expects different format: just run IDs array and url
        createAnnotationQueueRuns(
          {
            json: Object.keys(rowSelection),
            url: `${apiAnnotationQueuesPath}/${aq_id}/runs`,
          },
          {
            onSuccess: () => {
              setRowSelection({});
              navigate(
                `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${aq_id}`
              );
            },
            onError: (error) => {
              createToast({
                title: 'Error populating annotation queue',
                description: error.message,
                error: true,
              });
            },
          }
        );
      } else {
        // Other triggers use the standard format
        const aqTrigger = isAlignEvaluator
          ? alignEvaluatorTrigger
          : populateQueueTrigger;
        aqTrigger(
          {
            json: {
              session_ids: Object.keys(rowSelection),
              queue_id: aq_id,
            },
          },
          {
            onSuccess: () => {
              setRowSelection({});
              navigate(
                `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${aq_id}`
              );
            },
            onError: (error) => {
              createToast({
                title: 'Error populating annotation queue',
                description: error.message,
                error: true,
              });
            },
          }
        );
      }
    },
    [
      rowSelection,
      alignEvaluatorTrigger,
      createToast,
      navigate,
      setRowSelection,
      organizationId,
      populateQueueTrigger,
      sessionId,
      createAnnotationQueueRuns,
    ]
  );

  const selectedCount = Object.keys(rowSelection).length;
  const selectText = datasetId ? 'experiments' : 'runs';
  const selectLabel =
    !mutate.isMutating &&
    !isAlignEvaluatorMutating &&
    !isCreatingAnnotationQueueRuns
      ? `Select ${selectText} to add to annotation queue`
      : '';

  return (
    <FloatingActionBar
      label={selectLabel}
      onCancel={() => {
        setRowSelection({});
        if (setAlignmentMode) {
          setAlignmentMode(false);
        }
      }}
      cancelButtonProps={{ label: 'Close alignment mode' }}
    >
      {mutate.isMutating ? (
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <Text variant="md">Creating alignment queue</Text>
        </div>
      ) : isAlignEvaluatorMutating || isCreatingAnnotationQueueRuns ? (
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <Text variant="md">Adding {selectText} to queue for alignment</Text>
        </div>
      ) : (
        <>
          <Button
            size="md"
            color="primary"
            variant="outlined"
            className="grow"
            onClick={() => {
              if (runRuleId) {
                navigate(
                  datasetId
                    ? `/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}/${appEvaluatorPath}/${runRuleId}`
                    : `/${appOrganizationPath}/${organizationId}/${appProjectsPath}/${appSessionPath}/${sessionId}/${appEvaluatorPath}/${runRuleId}`
                );
              }
            }}
          >
            Go to Evaluator
          </Button>
          <Button
            size="md"
            color="primary"
            className={cn(
              'grow disabled:bg-brand-secondary disabled:text-[var(--brand-400)]'
            )}
            disabled={selectedCount === 0}
            leftDecorator={Edit03Icon}
            onClick={async () => {
              if (alignmentQueueId) {
                handlePopulateAnnotationQueue(alignmentQueueId, true);
              } else if (runRule) {
                const { session_id, dataset_id, ...rest } = runRule;
                let updatedRule;
                if (dataset_id) {
                  updatedRule = await mutate.trigger({
                    json: {
                      ...rest,
                      dataset_id,
                      create_alignment_queue: true,
                    },
                  });
                }
                if (session_id) {
                  updatedRule = await mutate.trigger({
                    json: {
                      ...rest,
                      session_id,
                      create_alignment_queue: true,
                    },
                  });
                }
                if (updatedRule && updatedRule.alignment_annotation_queue_id) {
                  handlePopulateAnnotationQueue(
                    updatedRule.alignment_annotation_queue_id,
                    true
                  );
                }
              } else {
                createToast({
                  title: 'Error',
                  description: 'No alignment queue found',
                  error: true,
                });
              }
            }}
          >
            Annotate for alignment
          </Button>
        </>
      )}
    </FloatingActionBar>
  );
};
